import type { AdapterChoice, AdapterInfo, AdapterTypeChoice } from '../../types';

/** ONE ADAPTER'S BOX: on or off, the request types it may run, and how many at once.
 *  Only what the adapter has the code for is tickable; the rest is greyed out with the
 *  reason (hover). A choice that went badly when measured (Google or Bing on crawl4ai)
 *  stays tickable with an amber warning — running it anyway is the admin's call. The
 *  dispatcher gives each request to the least busy adapter that may run it, so ticking
 *  a type on two adapters shares the load between them. */
export default function AdapterCard({
  info,
  value,
  onChange,
}: {
  info: AdapterInfo;
  value: AdapterChoice;
  onChange: (next: AdapterChoice) => void;
}) {
  const on = value.on;
  const types = new Set(value.types);
  const toggle = (t: string) => {
    const next = new Set(types);
    if (next.has(t)) next.delete(t);
    else next.add(t);
    onChange({ ...value, types: Array.from(next).sort() });
  };
  const groups: { title: string; items: AdapterTypeChoice[] }[] = [
    { title: 'Documents', items: info.choices.filter((c) => c.type === 'robots' || c.type === 'feed') },
    { title: 'Pages', items: info.choices.filter((c) => c.type === 'page') },
    { title: 'Search', items: info.choices.filter((c) => c.type.startsWith('search/')) },
    { title: 'Other', items: info.choices.filter((c) => c.type.startsWith('trends/')) },
  ];
  // Room is the admins' call for plain HTTP, crawl4ai and Serper; the browsers' is their tabs.
  const setsRoom = info.concurrency !== null;
  const room = info.devices
    ? `${info.devices.devices} device${info.devices.devices === 1 ? '' : 's'} online · ${info.devices.tabs} tabs`
    : `${info.room} at once`;

  return (
    <div
      className={`border border-gray-200 dark:border-gray-700 rounded-lg ${on ? '' : 'opacity-70'}`}
      data-testid={`adapter-${info.adapter}`}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{info.label}</span>
            {!info.set_up && (
              <span className="text-xs text-amber-700 dark:text-amber-400">Not set up: {info.not_set_up_because}</span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {on ? `${room} · ${info.busy} busy now` : 'Off: given no work. Its settings are kept.'}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={`${info.label} ${on ? 'on' : 'off'}`}
          onClick={() => onChange({ ...value, on: !on })}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
            on ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              on ? 'translate-x-5' : 'translate-x-0.5'
            }`}
          />
        </button>
      </div>
      <div className="px-4 pb-4 space-y-3 border-t border-gray-100 dark:border-gray-700 pt-3">
        {groups
          .filter((g) => g.items.some((c) => c.possible))
          .map((g) => (
            <div key={g.title}>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{g.title}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {g.items.map((c) => (
                  <label
                    key={c.type}
                    title={c.possible ? c.caution || '' : c.why_not}
                    className={`inline-flex items-center gap-1.5 text-sm ${
                      c.possible ? 'text-gray-800 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'
                    }`}
                  >
                    <input
                      type="checkbox"
                      disabled={!c.possible}
                      checked={c.possible && types.has(c.type)}
                      onChange={() => toggle(c.type)}
                    />
                    {c.label.replace(/^Search: /, '')}
                    {c.possible && c.caution && (
                      <span
                        className="text-amber-600 dark:text-amber-400"
                        aria-label={`Caution: ${c.caution}`}
                        data-testid={`caution-${c.type}`}
                      >
                        ⚠
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          ))}
        {setsRoom && (
          <div className="flex flex-wrap gap-4">
            <label className="text-xs text-gray-600 dark:text-gray-300">
              How many at once{' '}
              <input
                type="number"
                min={1}
                max={200}
                value={value.concurrency ?? ''}
                onChange={(e) =>
                  onChange({ ...value, concurrency: e.target.value === '' ? null : parseInt(e.target.value) })
                }
                className="ml-1 w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm"
              />
            </label>
            {info.per_host !== null && (
              <label className="text-xs text-gray-600 dark:text-gray-300">
                At once per site{' '}
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={value.per_host ?? ''}
                  onChange={(e) =>
                    onChange({ ...value, per_host: e.target.value === '' ? null : parseInt(e.target.value) })
                  }
                  className="ml-1 w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm"
                />
              </label>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
